import { Dialog } from "@progress/kendo-react-dialogs";
import { useState, useEffect, useCallback } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { TimePicker } from "@progress/kendo-react-dateinputs";
import { useQuery } from "react-query";
import { useToast } from "@chakra-ui/react";
import moment from "moment";
import { 
  chevronDoubleUpIcon,
  chevronUpIcon,
  equalIcon,
  chevronDownIcon 
} from '@progress/kendo-svg-icons';
import { SvgIcon } from "@progress/kendo-react-common";
import { enMessages } from "../../../messages/en-US";
import { useLocalization } from "@progress/kendo-react-intl";
import { 
  AlertDeliveryConfigUI, 
  convertAlertsSettingsCronToUI, 
  convertAlertsSettingsUIToCron
} from "../Utilities/CronConverter";
import alertsApiService from "../Utilities/AlertsApiService";
import { useAccountSettingsStore } from "../../../Zustand/accountSettingsStore";
import LoadingSpinner from "../../Shared/Components/LoadingSpinner";
import { ReactQueryKeys } from "../../../Constants/ClientRoutingConstants";

interface Props {
  onClose: () => void;
}

// Constants
const CRITICALITY_LEVELS = [
  { key: "critical", label: "Critical", icon: chevronDoubleUpIcon, color: "#780000" },
  { key: "high", label: "High", icon: chevronUpIcon, color: "#dc0000" },
  { key: "medium", label: "Medium", icon: equalIcon, color: "#fd8c00" },
  { key: "low", label: "Low", icon: chevronDownIcon, color: "#fdc500" }
] as const;

const FREQUENCY_OPTIONS = [
  { text: "Immediate", value: "immediate" },
  { text: "Every X Hours", value: "interval" },
  { text: "Every X Minutes", value: "intervalMinutes" },
  { text: "Daily", value: "daily" },
  { text: "Weekly", value: "weekly" }
];

const WEEKDAY_OPTIONS = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
].map(day => ({ text: day, value: day }));

const DEFAULT_CRON_SETTINGS = {
  criticalCron: "",
  highCron: "0 */6 * * *",
  mediumCron: "0 0 * * *",
  lowCron: "0 0 * * 1",
  timezone: "UTC"
};

const TOAST_CONFIG = {
  position: "bottom" as const,
  duration: 5000,
  isClosable: true
};

// Helper functions
const getTimezone = (accountTimezone: string | null | undefined): string => {
  return accountTimezone || moment.tz?.guess() || "UTC";
};

const getSchedulePreview = (settings: AlertDeliveryConfigUI): string => {
  const frequencyOption = FREQUENCY_OPTIONS.find(opt => opt.text === settings.frequency);
  const frequencyValue = frequencyOption?.value || "";
  
  switch (frequencyValue) {
    case "immediate":
      return "Delivered immediately when triggered";
    case "interval": {
      const deliveryTimes = calculateIntervalTimes(settings.interval, settings.time);
      return `Deliver every ${settings.interval} hours (${deliveryTimes.join(', ')})`;
    }
    case "intervalMinutes":
      return `Deliver every ${settings.interval} minutes starting at ${settings.time || "00:00"}`;
    case "daily":
      return `Daily at ${settings.time || "00:00"}`;
    case "weekly":
      return `Weekly on ${settings.weekday} at ${settings.time || "00:00"}`;
    default:
      return "";
  }
};

const calculateIntervalTimes = (interval: number | null, startTime: string | null): string[] => {
  if (!interval || !startTime) return [];
  
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const startTotalMinutes = startHour * 60 + startMinute;
  const times: string[] = [];
  
  for (let i = 0; i < 24; i += interval) {
    const totalMinutes = (startTotalMinutes + i * 60) % (24 * 60);
    const hours = Math.floor(totalMinutes / 60).toString().padStart(2, '0');
    const minutes = (totalMinutes % 60).toString().padStart(2, '0');
    times.push(`${hours}:${minutes}`);
  }
  
  const uniqueTimes = [...new Set(times)];
  const otherTimes = uniqueTimes.filter(t => t !== startTime).sort();
  return [startTime, ...otherTimes];
};

// Criticality Card Component
interface CriticalityCardProps {
  criticality: typeof CRITICALITY_LEVELS[number];
  settings: AlertDeliveryConfigUI;
  onUpdate: (field: keyof AlertDeliveryConfigUI, value: any) => void;
}

function CriticalityCard({ criticality, settings, onUpdate }: CriticalityCardProps) {
  const localizationService = useLocalization();
  const frequencyOption = FREQUENCY_OPTIONS.find(opt => opt.text === settings.frequency);
  const frequencyValue = frequencyOption?.value || "";

  const handleTimeChange = (e: any) => {
    if (e.target.value) {
      const timeString = e.target.value.toTimeString().slice(0, 5);
      onUpdate("time", timeString);
    }
  };

  return (
    <div className="k-card k-padding-lg k-bg-light p-4" style={{ minHeight: '170px' }}>
      <div className="k-d-flex k-align-items-center k-gap-sm k-margin-bottom-md">
        <SvgIcon icon={criticality.icon} size="large" style={{ color: criticality.color }} />
        <div className="text-lg font-medium">
          {criticality.label} {localizationService.toLanguageString("custom.alerts", enMessages.custom.alerts)}
        </div>
      </div>

      <div className="k-d-flex k-flex-col k-gap-lg">
        <div className="k-d-grid k-grid-cols-3 k-gap-lg">
          {/* Frequency */}
          <div>
            <label className="k-label k-margin-bottom-sm">
              {localizationService.toLanguageString("custom.frequency", enMessages.custom.frequency)}
            </label>
            <DropDownList
              data={FREQUENCY_OPTIONS}
              textField="text"
              dataItemKey="value"
              value={frequencyOption || null}
              onChange={(e) => {
                const updates: Partial<AlertDeliveryConfigUI> = { frequency: e.target.value.text };
                if (e.target.value.text === "Every X Minutes") {
                  updates.interval = 10;
                }
                Object.entries(updates).forEach(([key, value]) => {
                  onUpdate(key as keyof AlertDeliveryConfigUI, value);
                });
              }}
              className="k-w-100"
            />
          </div>

          {/* Time Picker */}
          {frequencyValue !== "immediate" && (
            <div>
              <label className="k-label k-margin-bottom-sm">
                {frequencyValue === "interval" || frequencyValue === "intervalMinutes"
                  ? localizationService.toLanguageString("custom.startTime", enMessages.custom.startTime)
                  : localizationService.toLanguageString("custom.time", enMessages.custom.time)
                }
              </label>
              <TimePicker
                value={settings.time ? new Date(`2000-01-01T${settings.time}`) : null}
                onChange={handleTimeChange}
                steps={{ hour: 1, minute: 1 }}
                format="HH:mm"
                className="k-w-100"
              />
            </div>
          )}

          {/* Interval Hours */}
          {frequencyValue === "interval" && (
            <div>
              <label className="k-label k-margin-bottom-sm">
                {localizationService.toLanguageString("custom.interval", enMessages.custom.interval)} ({localizationService.toLanguageString("custom.hours", enMessages.custom.hours)})
              </label>
              <NumericTextBox
                value={settings.interval}
                onChange={(e) => onUpdate("interval", e.target.value)}
                min={1}
                max={12}
                className="k-w-100"
              />
            </div>
          )}

          {/* Interval Minutes */}
          {frequencyValue === "intervalMinutes" && (
            <div>
              <label className="k-label k-margin-bottom-sm">
                {localizationService.toLanguageString("custom.interval", enMessages.custom.interval)} ({localizationService.toLanguageString("custom.minutes", enMessages.custom.minutes)})
              </label>
              <NumericTextBox
                value={settings.interval}
                onChange={(e) => onUpdate("interval", e.target.value)}
                min={1}
                max={59}
                className="k-w-100"
              />
            </div>
          )}

          {/* Weekday */}
          {frequencyValue === "weekly" && (
            <div>
              <label className="k-label k-margin-bottom-sm">
                {localizationService.toLanguageString("custom.weekday", enMessages.custom.weekday)}
              </label>
              <DropDownList
                data={WEEKDAY_OPTIONS}
                textField="text"
                dataItemKey="value"
                value={WEEKDAY_OPTIONS.find(opt => opt.text === settings.weekday) || null}
                onChange={(e) => onUpdate("weekday", e.target.value.text)}
                className="k-w-100"
              />
            </div>
          )}
        </div>

        {/* Schedule Preview */}
        <div className="k-margin-top-md">
          <label className="k-label k-margin-bottom-sm">
            {localizationService.toLanguageString("custom.schedulePreview", enMessages.custom.schedulePreview)}
          </label>
          <div className="k-text-muted k-bg-white k-padding-md k-border k-border-solid">
            {getSchedulePreview(settings)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AlertsSettingsDialog({ onClose }: Props) {
  const localizationService = useLocalization();
  const accountTimezone = useAccountSettingsStore((state) => state.accountTimezone);
  const toast = useToast(TOAST_CONFIG);

  const [settingsExist, setSettingsExist] = useState(false);
  const [alertSettings, setAlertSettings] = useState<Record<string, AlertDeliveryConfigUI> | null>(null);

  // Fetch settings
  const { data: backendSettings, isLoading } = useQuery(
    ReactQueryKeys.AlertsSettingsQuery,
    async () => {
      const settings = await alertsApiService.getAlertsSettings();
      setSettingsExist(settings !== null);
      return settings;
    },
    { retry: false }
  );

  // Initialize settings
  useEffect(() => {
    if (isLoading) return;
    
    const settings = backendSettings || {
      ...DEFAULT_CRON_SETTINGS,
      timezone: getTimezone(accountTimezone)
    };
    
    setAlertSettings(convertAlertsSettingsCronToUI(settings));
  }, [backendSettings, isLoading, accountTimezone]);

  // Update handler
  const updateSettings = useCallback((criticalityKey: string, field: keyof AlertDeliveryConfigUI, value: any) => {
    setAlertSettings(prev => prev ? {
      ...prev,
      [criticalityKey]: { ...prev[criticalityKey], [field]: value }
    } : null);
  }, []);

  // Save handler
  const [isSaving, setIsSaving] = useState(false);
  const handleSave = useCallback(async () => {
    if (!alertSettings) return;

    setIsSaving(true);
    try {
      const cronSettings = convertAlertsSettingsUIToCron(
        alertSettings,
        getTimezone(accountTimezone)
      );

      await alertsApiService.saveAlertsSettings(cronSettings, !settingsExist);
      setSettingsExist(true);
      onClose();
      toast({ status: "success", description: "Alerts settings saved successfully." });
    } catch (error) {
      toast({ status: "error", description: "Failed to save alerts settings." });
    } finally {
      setIsSaving(false);
    }
  }, [alertSettings, accountTimezone, settingsExist, onClose, toast]);

  // Reset handler
  const handleReset = useCallback(() => {
    const defaultSettings = {
      ...DEFAULT_CRON_SETTINGS,
      timezone: getTimezone(accountTimezone)
    };
    setAlertSettings(convertAlertsSettingsCronToUI(defaultSettings));
  }, [accountTimezone]);

  if (isLoading || !alertSettings) {
    return (
      <Dialog
        title={localizationService.toLanguageString("custom.alertDeliverySettings", enMessages.custom.alertDeliverySettings)}
        onClose={onClose}
        className="reservoir-view"
        height="90vh"
        width="800px"
      >
        <div className="k-padding-md k-d-flex k-justify-content-center k-align-items-center" style={{ height: '100%' }}>
          <LoadingSpinner />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      title={localizationService.toLanguageString("custom.alertDeliverySettings", enMessages.custom.alertDeliverySettings)}
      onClose={onClose}
      className="reservoir-view"
      height="90vh"
      width="800px"
    >
      <div className="k-padding-md k-d-flex k-flex-col" style={{ height: '100%' }}>
        <div className="k-margin-bottom-lg text-base">
          {localizationService.toLanguageString("custom.configureAlertSettings", enMessages.custom.configureAlertSettings)}
        </div>

        <div 
          className="k-d-flex k-flex-col k-gap-md k-flex-1 p-2" 
          style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}
        >
          {CRITICALITY_LEVELS.map(criticality => (
            <CriticalityCard
              key={criticality.key}
              criticality={criticality}
              settings={alertSettings[criticality.key]}
              onUpdate={(field, value) => updateSettings(criticality.key, field, value)}
            />
          ))}
        </div>

        <div className="k-form-separator"></div>
        <div className="flex justify-between k-form-button">
          <div className="p-2">
            <Button themeColor="dark" onClick={handleReset}>
              {localizationService.toLanguageString("custom.reset", enMessages.custom.reset)}
            </Button>
          </div>
          <div className="p-2">
            <Button themeColor="base" onClick={onClose}>
              {localizationService.toLanguageString("custom.cancel", enMessages.custom.cancel)}
            </Button>
            <Button themeColor="primary" onClick={handleSave} disabled={isSaving} style={{ marginLeft: '5px' }}>
              {localizationService.toLanguageString("custom.save", enMessages.custom.save)}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
